import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { ROUTES } from '../../../constants/routes';
import { userHasFn, SUB_APPS } from '../../../utils/rbac';
import { inventoryApi } from '../api';

const menuItems = ['Import', 'Types', 'Statuses', 'Storage locations'];

function splitCsvRow(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (c === ',' && !inQuotes) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur.trim());
  return out.map((s) => {
    let t = s;
    if (t.startsWith('"') && t.endsWith('"') && t.length >= 2) {
      t = t.slice(1, -1).replace(/""/g, '"');
    }
    return t.trim();
  });
}

function parseInventoryImportCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { error: 'File needs a header row plus at least one data row.' };
  }
  const headers = splitCsvRow(lines[0].replace(/^\uFEFF/, '')).map((h) => h.trim().toLowerCase());
  const idx = (name, alts = []) => {
    const all = [name, ...alts];
    for (const key of all) {
      const i = headers.indexOf(key);
      if (i >= 0) return i;
    }
    return -1;
  };
  const iCode = idx('property_code', ['property code', 'property']);
  const iName = idx('name');
  const iType = idx('type');
  if (iCode === -1 || iName === -1 || iType === -1) {
    return {
      error: `Missing required columns. Need property_code, name, type. Found: ${headers.join(', ')}`,
    };
  }
  const iDesc = idx('description');
  const iCost = idx('cost');
  const iQty = idx('quantity', ['qty']);
  const iStatus = idx('status');
  const iStorage = idx('storage_location', ['storage location', 'storage']);

  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = splitCsvRow(lines[i]);
    rows.push({
      property_code: parts[iCode] || '',
      name: parts[iName] || '',
      type: parts[iType] || '',
      description: iDesc >= 0 ? parts[iDesc] || '' : '',
      cost: iCost >= 0 ? parts[iCost] || '' : '',
      quantity: iQty >= 0 ? parts[iQty] || '1' : '1',
      status: iStatus >= 0 ? parts[iStatus] || 'inventory' : 'inventory',
      storage_location: iStorage >= 0 ? parts[iStorage] || '' : '',
    });
  }
  return { rows };
}

function downloadImportTemplate() {
  const csv =
    'property_code,name,type,description,cost,quantity,status,storage_location\n'
    + 'PROP001,Light bulb,Electrical,60W LED,2.50,100,inventory,Maintenance Closet\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'inventory-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function InventoryAdmin() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('Import');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const [properties, setProperties] = useState([]);
  const [typePropertyFilter, setTypePropertyFilter] = useState('');
  const [storagePropertyFilter, setStoragePropertyFilter] = useState('');

  const [types, setTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [storageLocations, setStorageLocations] = useState([]);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importParseError, setImportParseError] = useState('');
  const [importRows, setImportRows] = useState([]);
  const [importSummary, setImportSummary] = useState(null);

  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [showEditTypeModal, setShowEditTypeModal] = useState(false);
  const [newType, setNewType] = useState({ name: '', propertyId: '' });
  const [editType, setEditType] = useState(null);

  const [showAddStatusModal, setShowAddStatusModal] = useState(false);
  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState({ key: '', name: '', sortOrder: 100 });
  const [editStatus, setEditStatus] = useState(null);

  const [showAddStorageModal, setShowAddStorageModal] = useState(false);
  const [showEditStorageModal, setShowEditStorageModal] = useState(false);
  const [newStorage, setNewStorage] = useState({ name: '', description: '', propertyId: '' });
  const [editStorage, setEditStorage] = useState(null);

  useEffect(() => {
    if (user && !userHasFn(user, SUB_APPS.INVENTORY, 'admin')) {
      navigate(ROUTES.INVENTORY);
    }
  }, [user, navigate]);

  useEffect(() => {
    inventoryApi.properties()
      .then((res) => {
        setProperties(res.data);
        if (res.data.length === 1) {
          const id = String(res.data[0].id);
          setTypePropertyFilter(id);
          setStoragePropertyFilter(id);
        }
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load properties'));
  }, []);

  const loadStatuses = useCallback(async () => {
    setStatusesLoading(true);
    try {
      const res = await inventoryApi.adminStatuses();
      setStatuses(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load statuses');
      setStatuses([]);
    } finally {
      setStatusesLoading(false);
    }
  }, []);

  const loadTypes = useCallback(async (propertyId) => {
    if (!propertyId) {
      setTypes([]);
      return;
    }
    const res = await inventoryApi.adminTypes(propertyId);
    setTypes(res.data);
  }, []);

  const loadStorage = useCallback(async (propertyId) => {
    if (!propertyId) {
      setStorageLocations([]);
      return;
    }
    const res = await inventoryApi.adminStorageLocations(propertyId);
    setStorageLocations(res.data);
  }, []);

  useEffect(() => {
    if (activeMenu === 'Statuses') loadStatuses();
  }, [activeMenu, loadStatuses]);

  useEffect(() => {
    if (activeMenu === 'Types') {
      loadTypes(typePropertyFilter).catch((e) => setError(e.response?.data?.error || 'Failed to load types'));
    }
  }, [activeMenu, typePropertyFilter, loadTypes]);

  useEffect(() => {
    if (activeMenu === 'Storage locations') {
      loadStorage(storagePropertyFilter).catch((e) => setError(e.response?.data?.error || 'Failed to load storage locations'));
    }
  }, [activeMenu, storagePropertyFilter, loadStorage]);

  const clearAlerts = () => {
    setError('');
    setMsg('');
  };

  const renderPropertyFilter = (filterValue, setFilter, count, itemLabel) => (
    <div className="row g-2 mb-3">
      <div className="col-md-4">
        <label className="form-label small mb-1">Filter by property</label>
        <select
          className="form-select form-select-sm"
          value={filterValue}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="">Select property…</option>
          {properties.map((property) => (
            <option key={property.id} value={property.id}>
              {property.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4 d-flex align-items-end">
        <span className="small text-muted">
          {count} {itemLabel}{count === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportParseError('');
    setImportSummary(null);
    setImportRows([]);
    try {
      const text = await file.text();
      const parsed = parseInventoryImportCsv(text);
      if (parsed.error) {
        setImportParseError(parsed.error);
        return;
      }
      setImportRows(parsed.rows);
    } catch {
      setImportParseError('Could not read CSV file.');
    }
  };

  const runImport = async () => {
    setBusy(true);
    setError('');
    setMsg('');
    try {
      const res = await inventoryApi.adminImportItems(importRows);
      setImportSummary(res.data);
      setMsg(`Import complete: ${res.data.created} created, ${res.data.skipped} skipped.`);
      setImportRows([]);
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await inventoryApi.adminCreateType({
        propertyId: Number(newType.propertyId),
        name: newType.name.trim(),
      });
      setShowAddTypeModal(false);
      setNewType({ name: '', propertyId: '' });
      setMsg('Type added.');
      await loadTypes(typePropertyFilter || newType.propertyId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add type');
    } finally {
      setBusy(false);
    }
  };

  const handleEditType = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await inventoryApi.adminUpdateType(editType.id, {
        name: editType.name.trim(),
        isActive: editType.is_active,
      });
      setShowEditTypeModal(false);
      setEditType(null);
      setMsg('Type updated.');
      await loadTypes(typePropertyFilter);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update type');
    } finally {
      setBusy(false);
    }
  };

  const removeType = async (row) => {
    if (!window.confirm(`Delete type "${row.name}"?`)) return;
    try {
      await inventoryApi.adminDeleteType(row.id);
      setMsg('Type deleted.');
      await loadTypes(typePropertyFilter);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleAddStatus = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await inventoryApi.adminCreateStatus(newStatus);
      setShowAddStatusModal(false);
      setNewStatus({ key: '', name: '', sortOrder: 100 });
      setMsg('Status added.');
      await loadStatuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add status');
    } finally {
      setBusy(false);
    }
  };

  const handleEditStatus = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await inventoryApi.adminUpdateStatus(editStatus.id, {
        name: editStatus.name.trim(),
        sortOrder: Number(editStatus.sort_order),
        isActive: editStatus.is_active,
      });
      setShowEditStatusModal(false);
      setEditStatus(null);
      setMsg('Status updated.');
      await loadStatuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const removeStatus = async (row) => {
    if (row.is_system) return;
    if (!window.confirm(`Delete status "${row.name}"?`)) return;
    try {
      await inventoryApi.adminDeleteStatus(row.id);
      setMsg('Status deleted.');
      await loadStatuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleAddStorage = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await inventoryApi.adminCreateStorageLocation({
        propertyId: Number(newStorage.propertyId),
        name: newStorage.name.trim(),
        description: newStorage.description.trim() || null,
      });
      setShowAddStorageModal(false);
      setNewStorage({ name: '', description: '', propertyId: '' });
      setMsg('Storage location added.');
      await loadStorage(storagePropertyFilter || newStorage.propertyId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add storage location');
    } finally {
      setBusy(false);
    }
  };

  const handleEditStorage = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await inventoryApi.adminUpdateStorageLocation(editStorage.id, {
        name: editStorage.name.trim(),
        description: editStorage.description || null,
        isActive: editStorage.is_active,
      });
      setShowEditStorageModal(false);
      setEditStorage(null);
      setMsg('Storage location updated.');
      await loadStorage(storagePropertyFilter);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update storage location');
    } finally {
      setBusy(false);
    }
  };

  const removeStorage = async (row) => {
    if (!window.confirm(`Delete storage location "${row.name}"?`)) return;
    try {
      await inventoryApi.adminDeleteStorageLocation(row.id);
      setMsg('Storage location deleted.');
      await loadStorage(storagePropertyFilter);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'Import':
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Item Import</h3>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  clearAlerts();
                  setImportParseError('');
                  setImportRows([]);
                  setImportSummary(null);
                  setShowImportModal(true);
                }}
              >
                Import from CSV
              </button>
            </div>
            <p className="text-muted small mb-0">
              Bulk-create inventory items from CSV. Columns: property_code, name, type, description, cost,
              quantity, status, storage_location.
            </p>
            {importSummary && (
              <div className="alert alert-info mt-3 mb-0 small">
                <strong>{importSummary.created}</strong> created,{' '}
                <strong>{importSummary.skipped}</strong> skipped.
                {importSummary.errors?.length > 0 && (
                  <span> See import dialog for row errors.</span>
                )}
              </div>
            )}
          </div>
        );

      case 'Types':
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Type Management</h3>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!typePropertyFilter}
                onClick={() => {
                  clearAlerts();
                  setNewType({ name: '', propertyId: typePropertyFilter });
                  setShowAddTypeModal(true);
                }}
              >
                Add Type
              </button>
            </div>
            <p className="text-muted small">Inventory categories scoped to each property (e.g. Electrical, Plumbing).</p>
            {renderPropertyFilter(typePropertyFilter, setTypePropertyFilter, types.length, 'type')}
            {!typePropertyFilter ? (
              <p className="text-muted">Select a property to view types.</p>
            ) : (
              <div className="app-table-wrap table-responsive">
                <table className="table table-striped mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {types.length > 0 ? (
                      types.map((row) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.name}</td>
                          <td>{row.is_active ? 'Yes' : 'No'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => {
                                clearAlerts();
                                setEditType({ ...row });
                                setShowEditTypeModal(true);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeType(row)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center text-muted">No types found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case 'Statuses':
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Status Management</h3>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  clearAlerts();
                  setNewStatus({ key: '', name: '', sortOrder: 100 });
                  setShowAddStatusModal(true);
                }}
              >
                Add Status
              </button>
            </div>
            <p className="text-muted small">
              Item lifecycle states (inventory, installed, replaced). Sort order controls dropdown sequence — not quantity.
            </p>
            <div className="app-table-wrap table-responsive">
              <table className="table table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Key</th>
                    <th>Display name</th>
                    <th>Sort order</th>
                    <th>System</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statusesLoading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">Loading…</td>
                    </tr>
                  ) : statuses.length > 0 ? (
                    statuses.map((row) => (
                      <tr key={row.id}>
                        <td><code>{row.key}</code></td>
                        <td>{row.name}</td>
                        <td>{row.sort_order}</td>
                        <td>{row.is_system ? 'Yes' : 'No'}</td>
                        <td>{row.is_active ? 'Yes' : 'No'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => {
                              clearAlerts();
                              setEditStatus({ ...row });
                              setShowEditStatusModal(true);
                            }}
                          >
                            Edit
                          </button>
                          {!row.is_system && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeStatus(row)}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">No statuses found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'Storage locations':
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Storage Location Management</h3>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!storagePropertyFilter}
                onClick={() => {
                  clearAlerts();
                  setNewStorage({ name: '', description: '', propertyId: storagePropertyFilter });
                  setShowAddStorageModal(true);
                }}
              >
                Add Storage Location
              </button>
            </div>
            <p className="text-muted small">Where stock is kept (closets, bins, warehouses) — separate from site locations.</p>
            {renderPropertyFilter(storagePropertyFilter, setStoragePropertyFilter, storageLocations.length, 'location')}
            {!storagePropertyFilter ? (
              <p className="text-muted">Select a property to view storage locations.</p>
            ) : (
              <div className="app-table-wrap table-responsive">
                <table className="table table-striped mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storageLocations.length > 0 ? (
                      storageLocations.map((row) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.name}</td>
                          <td>{row.description || '—'}</td>
                          <td>{row.is_active ? 'Yes' : 'No'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => {
                                clearAlerts();
                                setEditStorage({ ...row });
                                setShowEditStorageModal(true);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeStorage(row)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">No storage locations found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const closeAllModals = () => {
    setShowImportModal(false);
    setShowAddTypeModal(false);
    setShowEditTypeModal(false);
    setShowAddStatusModal(false);
    setShowEditStatusModal(false);
    setShowAddStorageModal(false);
    setShowEditStorageModal(false);
  };

  const isAnyModalOpen =
    showImportModal ||
    showAddTypeModal ||
    showEditTypeModal ||
    showAddStatusModal ||
    showEditStatusModal ||
    showAddStorageModal ||
    showEditStorageModal;

  return (
    <div className="app-shell-page">
      {error && <div className="alert alert-danger">{error}</div>}
      {msg && <div className="alert alert-success">{msg}</div>}

      <div className="row g-4 admin-sidebar-layout">
        <div className="col-md-3">
          <div className="card app-card">
            <div className="card-header bg-transparent border-bottom py-3">
              <h5 className="mb-0">Admin Menu</h5>
            </div>
            <div className="list-group list-group-flush">
              {menuItems.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`list-group-item list-group-item-action ${activeMenu === item ? 'active' : ''}`}
                  onClick={() => {
                    setActiveMenu(item);
                    clearAlerts();
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="col-md-9">
          <div className="card app-card">
            <div className="card-body">{renderContent()}</div>
          </div>
        </div>
      </div>

      {showImportModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import inventory items</h5>
                <button type="button" className="btn-close" onClick={() => setShowImportModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  CSV columns: property_code, name, type, description, cost, quantity, status, storage_location.
                </p>
                <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={downloadImportTemplate}>
                  Download sample CSV
                </button>
                <div className="mb-3">
                  <label className="form-label">CSV file</label>
                  <input type="file" accept=".csv,text/csv" className="form-control" onChange={handleImportFile} />
                </div>
                {importParseError && <div className="alert alert-danger py-2 small">{importParseError}</div>}
                {importRows.length > 0 && !importParseError && (
                  <div className="alert alert-info py-2 small mb-0">
                    Ready to import <strong>{importRows.length}</strong> row{importRows.length === 1 ? '' : 's'}.
                  </div>
                )}
                {importSummary && (
                  <div className="mt-3">
                    <p className="mb-2 small">
                      <strong>{importSummary.created}</strong> created,{' '}
                      <strong>{importSummary.skipped}</strong> skipped.
                    </p>
                    {importSummary.errors?.length > 0 && (
                      <div className="table-responsive border rounded" style={{ maxHeight: 280, overflow: 'auto' }}>
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr><th>Row</th><th>Error</th></tr>
                          </thead>
                          <tbody>
                            {importSummary.errors.slice(0, 50).map((er) => (
                              <tr key={`${er.line}-${er.message}`}>
                                <td>{er.line}</td>
                                <td className="small">{er.message}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowImportModal(false)}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy || !importRows.length || !!importParseError}
                  onClick={runImport}
                >
                  {busy ? 'Importing…' : 'Run import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddTypeModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleAddType}>
              <div className="modal-header">
                <h5 className="modal-title">Add New Type</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddTypeModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Property</label>
                  <select
                    className="form-select"
                    value={newType.propertyId}
                    onChange={(e) => setNewType({ ...newType, propertyId: e.target.value })}
                    required
                  >
                    <option value="">Select property…</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Type name</label>
                  <input
                    className="form-control"
                    value={newType.name}
                    onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddTypeModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Create Type'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditTypeModal && editType && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleEditType}>
              <div className="modal-header">
                <h5 className="modal-title">Edit Type</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditTypeModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Type name</label>
                  <input
                    className="form-control"
                    value={editType.name}
                    onChange={(e) => setEditType({ ...editType, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-check">
                  <input
                    id="edit-type-active"
                    type="checkbox"
                    className="form-check-input"
                    checked={editType.is_active}
                    onChange={(e) => setEditType({ ...editType, is_active: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="edit-type-active">Active</label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditTypeModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddStatusModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleAddStatus}>
              <div className="modal-header">
                <h5 className="modal-title">Add New Status</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddStatusModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Key</label>
                  <input
                    className="form-control"
                    placeholder="e.g. on_order"
                    value={newStatus.key}
                    onChange={(e) => setNewStatus({ ...newStatus, key: e.target.value })}
                    required
                  />
                  <div className="form-text">Lowercase slug for CSV/API. Cannot use inventory, installed, or replaced.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Display name</label>
                  <input
                    className="form-control"
                    value={newStatus.name}
                    onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Sort order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={newStatus.sortOrder}
                    onChange={(e) => setNewStatus({ ...newStatus, sortOrder: Number(e.target.value) })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddStatusModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Create Status'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditStatusModal && editStatus && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleEditStatus}>
              <div className="modal-header">
                <h5 className="modal-title">Edit Status</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditStatusModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Key</label>
                  <input className="form-control" value={editStatus.key} readOnly disabled />
                </div>
                <div className="mb-3">
                  <label className="form-label">Display name</label>
                  <input
                    className="form-control"
                    value={editStatus.name}
                    onChange={(e) => setEditStatus({ ...editStatus, name: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Sort order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editStatus.sort_order}
                    onChange={(e) => setEditStatus({ ...editStatus, sort_order: Number(e.target.value) })}
                  />
                  <div className="form-text">List order in dropdowns — not item quantity.</div>
                </div>
                <div className="form-check">
                  <input
                    id="edit-status-active"
                    type="checkbox"
                    className="form-check-input"
                    checked={editStatus.is_active}
                    onChange={(e) => setEditStatus({ ...editStatus, is_active: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="edit-status-active">Active</label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditStatusModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddStorageModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleAddStorage}>
              <div className="modal-header">
                <h5 className="modal-title">Add Storage Location</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddStorageModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Property</label>
                  <select
                    className="form-select"
                    value={newStorage.propertyId}
                    onChange={(e) => setNewStorage({ ...newStorage, propertyId: e.target.value })}
                    required
                  >
                    <option value="">Select property…</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={newStorage.name}
                    onChange={(e) => setNewStorage({ ...newStorage, name: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <input
                    className="form-control"
                    value={newStorage.description}
                    onChange={(e) => setNewStorage({ ...newStorage, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddStorageModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditStorageModal && editStorage && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleEditStorage}>
              <div className="modal-header">
                <h5 className="modal-title">Edit Storage Location</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditStorageModal(false)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={editStorage.name}
                    onChange={(e) => setEditStorage({ ...editStorage, name: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <input
                    className="form-control"
                    value={editStorage.description || ''}
                    onChange={(e) => setEditStorage({ ...editStorage, description: e.target.value })}
                  />
                </div>
                <div className="form-check">
                  <input
                    id="edit-storage-active"
                    type="checkbox"
                    className="form-check-input"
                    checked={editStorage.is_active}
                    onChange={(e) => setEditStorage({ ...editStorage, is_active: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="edit-storage-active">Active</label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditStorageModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isAnyModalOpen && (
        <div className="modal-backdrop fade show" onClick={closeAllModals} aria-hidden="true" />
      )}
    </div>
  );
}

export default InventoryAdmin;
